import { ColumnDef, ColumnMeta } from '@tanstack/react-table';
import get from 'lodash/get';
import set from 'lodash/set';

import {
  extractModificationPath,
  getModificationFieldKey,
  isRelativeModificationType,
} from 'services/webedi/modificationGroupHelpers';
import { normalizePath } from 'services/webedi/webedi';

export const flattenMultiColumnColumns = <TData, TValue>(
  columns: Array<ColumnDef<TData, TValue>>,
): Array<ColumnMeta<TData, TValue> & { id: string }> => {
  const result: Array<ColumnMeta<TData, TValue> & { id: string }> = [];

  columns.forEach((column) => {
    if ('columns' in column && Array.isArray(column.columns)) {
      result.push(...flattenMultiColumnColumns(column.columns));
    } else if (column.meta && column.id) {
      result.push({ ...column.meta, id: column.id });
    }
  });

  return result;
};

export const expandWildcardPath = (columnId: string, data: unknown): Array<string> => {
  if (!columnId.includes('.*.')) {
    return [columnId];
  }

  const [arrayPath, remainingPath] = columnId.split('.*.');
  const arrayData = get(data, arrayPath);

  if (!Array.isArray(arrayData) || arrayData.length === 0) {
    const expandedPath = normalizePath(`${arrayPath}.0.${remainingPath}`);
    return expandWildcardPath(expandedPath, data);
  }

  return arrayData.flatMap((_item, arrayIndex) => {
    const expandedPath = normalizePath(`${arrayPath}.${arrayIndex}.${remainingPath}`);
    return expandWildcardPath(expandedPath, data);
  });
};

export const resolveModificationGroupPath = (path: string, data: unknown): string => {
  const pathInfo = extractModificationPath(path);
  if (!pathInfo) {
    return path;
  }

  const typeValue = get(data, `${pathInfo.basePath}.type`);
  const isRelative = isRelativeModificationType(typeValue);

  return getModificationFieldKey(path, isRelative);
};

export const setBulkValue = (data: unknown, columnId: string, value: string | number): void => {
  const paths = expandWildcardPath(columnId, data);

  paths.forEach((path) => {
    const resolvedPath = resolveModificationGroupPath(path, data);
    set(data as NonNullable<unknown>, resolvedPath, value);
  });
};
